import { DisplayRefDisplayType, EBIcon } from '@repo/sdk/models/components';

import { NavLink } from '@/components/navigation/nav-link';
import { ActionIcon } from '@/features/equipment-browser/action-components/action-icon';
import { useGetReadingsValues } from '@/features/equipment-browser/subscriptions/use-get-readings-values';
import { useGetSettingsValues } from '@/features/equipment-browser/subscriptions/use-get-settings-values';
import { extractIconRefs } from '@/helpers/extract-refs';

import { getDeviceIconConfig } from './get-device-icon-config';
import { useDeviceAlarmIconConfig } from './use-device-alarm-icon-config';

type DeviceAlarmIconProps = {
  deviceId: string;
  fallbackIcon?: string;
  /** When true, render nothing if no active icon state or alarm (e.g. facility map secondary icon). */
  hideWhenInactive?: boolean;
  icon: EBIcon;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** When true, marks the icon as decorative for screen readers (aria-hidden). */
  'aria-hidden'?: boolean;
};

/**
 * Returns the device icon component
 * @param deviceId - The ID of the device
 * @param fallbackIcon - The fallback icon to use if the device icon is not found
 * @param icon - The icon to use for the device
 * @param size - The size of the icon
 * @param aria-hidden - When true, marks the icon as decorative for screen readers
 * @returns The device icon component
 */
export const DeviceIcon = ({
  deviceId,
  fallbackIcon = 'Settings',
  hideWhenInactive = false,
  icon,
  size = 'md',
  'aria-hidden': ariaHidden,
}: DeviceAlarmIconProps) => {
  // Extract refs from icon and reactively subscribe to their values
  const refs = extractIconRefs(icon);
  useGetReadingsValues(
    refs.readingRefs.map((ref) => ({ displayType: DisplayRefDisplayType.Label, ref, label: '' }))
  );
  useGetSettingsValues(
    refs.settingRefs.map((ref) => ({ displayType: DisplayRefDisplayType.Label, ref, label: '' }))
  );

  // Get the alarm icon config, if there are any alarms, else this will be undefined
  const alarmIconConfig = useDeviceAlarmIconConfig(deviceId);
  // getDeviceIconConfig will read the current reactive values
  const deviceIconConfig =
    alarmIconConfig ?? getDeviceIconConfig(icon, fallbackIcon, hideWhenInactive);

  if (!deviceIconConfig) return null;

  const linkId = deviceIconConfig.actions?.find(
    (action) => action.linkAction !== undefined && action.linkAction?.id !== undefined
  )?.linkAction?.id;

  const IconComponent = (
    <ActionIcon
      fallbackIcon={fallbackIcon}
      icon={deviceIconConfig.icon}
      isPulsing={deviceIconConfig.isPulsing}
      size={size}
      tooltipContent={deviceIconConfig.tooltipContent?.value}
      variant={deviceIconConfig.variant}
    />
  );
  const content = linkId ? (
    <NavLink routeId={linkId} onClick={(e) => e.stopPropagation()}>
      {IconComponent}
    </NavLink>
  ) : (
    IconComponent
  );

  if (ariaHidden === true) {
    return <span aria-hidden>{content}</span>;
  }
  return content;
};
